// P3 (clinic-live-card-visits): the card's visits with live Raintree states.
// Rendered only when the live panel is on for this card (the endpoint answered
// enabled); otherwise CRMQueue renders today's list unchanged.
// Layout (Matt's pick, Oct 24): the next visit up top, the rest as a list.
// Reschedule and Cancel are the card's own handlers (same prompt, route and
// toast as #51); they show only on visits still pending in Raintree and ahead,
// and only while the answer belongs to the card's own patient.
import React, { useState } from "react";
import { History, MoreHorizontal, RefreshCw, MessageSquare, AlertTriangle, X } from "lucide-react";
import { formatWallClock, isWallClockPast } from "../lib/wallClock";
import { splitVisits, chipClass, liveStatusLine, fallbackRows, PAST_SHOWN, showYear, shortClinic } from "../lib/liveVisits";

const WHEN_OPTS = { weekday: "short", month: "short", day: "numeric", hour: "numeric", minute: "2-digit" };
const ROW_DATE = { weekday: "short", month: "short", day: "numeric" };
const ROW_TIME = { hour: "numeric", minute: "2-digit" };

function when(v) {
  return formatWallClock(v, WHEN_OPTS).replace(/, (\d{1,2}:\d{2}\s?[AP]M)$/, " at $1");
}

function shortDay(iso) {
  if (!iso) return null;
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? null : d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
}

function Chip({ row }) {
  if (!row.state_label) return null;
  return (
    <span className={`text-xs px-1.5 py-0.5 rounded font-medium ${chipClass(row.state)}`} data-testid={`visit-chip-${row.key}`}>
      {row.state_label}
    </span>
  );
}

function TextLine({ row }) {
  const c = shortDay(row.text_confirmed_at);
  const r = shortDay(row.text_reminder_at);
  if (!c && !r) return null;
  return (
    <p className="text-xs flex flex-wrap items-center gap-x-1.5" data-testid={`visit-texts-${row.key}`}>
      {c && <span className="inline-flex items-center gap-1 text-emerald-700"><MessageSquare size={11} />Patient confirmed by text · {c}</span>}
      {c && r && <span className="text-slate-400">·</span>}
      {r && <span className="text-slate-500">Reminder sent · {r}</span>}
    </p>
  );
}

function BookedBy({ row }) {
  if (!row.booked_by_label) return null;
  return <p className="text-xs text-slate-400" data-testid={`visit-booked-${row.key}`}>{row.booked_by_label}</p>;
}

// "PT evaluation (PTEVA)": the plain name, then the code, muted. An unmapped
// code shows alone.
function TypeText({ row }) {
  const code = (row.appointment_type || "").toUpperCase();
  if (!row.type_name) return <>{code || row.type_label || ""}</>;
  return <>{row.type_name} <span className="text-[0.85em] text-slate-400">({code})</span></>;
}

export default function CardVisitsPanel({
  live, rows, stored, checking, actionsAllowed, clinicFor, onRefresh, onReschedule, onCancel, cancellingApptId,
  notice, onDismissNotice,
}) {
  const [allPast, setAllPast] = useState(false);
  const [showCancelled, setShowCancelled] = useState(false);
  const [openRow, setOpenRow] = useState(null);
  const [menuFor, setMenuFor] = useState(null);
  // No usable live answer yet (checking) or none at all (Raintree couldn't be
  // checked): the stored visits in the same layout, without states.
  const shown = rows || fallbackRows(stored, isWallClockPast);
  const { next, upcoming, past, cancelled } = splitVisits(shown, isWallClockPast);
  // W3 item 8: a flagged card shows the panel before the first answer arrives
  const status = live ? liveStatusLine(live, checking) : "Checking Raintree…";
  const nextClinic = next ? clinicFor(next) : null;
  // Buttons only on visits still pending in Raintree and ahead, and only while
  // the answer is for the card's own patient (actionsAllowed).
  const can = (row) => (rows ? actionsAllowed : true) && row.actions === true && row.appointment_id != null;
  const clinicText = (c, row) => (c && c.name ? `${c.name}${row.partner_clinic ? " (partner clinic)" : ""}` : null);

  const buttons = (row, small) => (
    <>
      <button type="button" onClick={(e) => { e.stopPropagation(); e.preventDefault(); setMenuFor(null); onReschedule(row); }}
        className={`text-xs ${small ? "block w-full text-left px-3 py-1.5 hover:bg-slate-50" : "px-2 py-1 rounded bg-blue-100 text-blue-700 hover:bg-blue-200"} font-medium`}
        data-testid={`reschedule-appt-${row.appointment_id}`}>Reschedule</button>
      <button type="button" disabled={cancellingApptId === row.appointment_id}
        onClick={(e) => { e.stopPropagation(); e.preventDefault(); setMenuFor(null); onCancel(row, isWallClockPast(row.appointment_datetime)); }}
        className={`text-xs ${small ? "block w-full text-left px-3 py-1.5 hover:bg-slate-50 text-red-700" : "px-2 py-1 rounded bg-red-100 text-red-700 hover:bg-red-200"} font-medium disabled:opacity-50`}
        data-testid={`cancel-appt-${row.appointment_id}`}>{cancellingApptId === row.appointment_id ? "..." : "Cancel"}</button>
    </>
  );

  // W3 item 13: one line per visit (date, time, plain type, therapist's surname when
  // names are on, clinic without the WestStar prefix), the chip on the right; the
  // year only outside the current year; full details on hover (title) or tap.
  const lineText = (row) => {
    const clinic = clinicFor(row);
    const dateOpts = showYear(row.appointment_datetime) ? { ...ROW_DATE, year: "numeric" } : ROW_DATE;
    return [
      formatWallClock(row.appointment_datetime, dateOpts),
      formatWallClock(row.appointment_datetime, ROW_TIME),
      row.type_name || (row.appointment_type || "").toUpperCase() || row.type_label || null,
      row.provider_surname || null,
      shortClinic(clinic && clinic.name, row.partner_clinic),
    ].filter(Boolean).join(" · ");
  };
  const details = (row) => {
    const code = (row.appointment_type || "").toUpperCase();
    const c = shortDay(row.text_confirmed_at);
    const r = shortDay(row.text_reminder_at);
    return [
      `${formatWallClock(row.appointment_datetime, { ...ROW_DATE, year: "numeric" })} · ${formatWallClock(row.appointment_datetime, ROW_TIME)}`,
      row.type_name ? `${row.type_name} (${code})` : code || null,
      row.provider_name ? `Therapist: ${row.provider_name}` : null,
      clinicText(clinicFor(row), row),
      row.booked_by_label || null,
      c ? `Patient confirmed by text · ${c}` : null,
      r ? `Reminder sent · ${r}` : null,
    ].filter(Boolean);
  };

  const Row = ({ row, inUpcoming }) => {
    const open = openRow === row.key;
    const info = details(row);
    return (
      <div className="py-1.5 border-t border-purple-100" data-testid={`visit-row-${row.key}`}>
        <div className="flex items-center justify-between gap-3 cursor-pointer" title={info.join("\n")}
          onClick={(e) => { e.stopPropagation(); e.preventDefault(); setOpenRow(open ? null : row.key); }}>
          <p className="text-xs text-purple-800 min-w-0 flex-1 truncate" data-testid={`visit-line-${row.key}`}>{lineText(row)}</p>
          <div className="flex items-center gap-1.5 relative shrink-0">
            {row.state_label ? <Chip row={row} /> : !rows ? <span className="text-xs text-slate-400" data-testid={`visit-chip-${row.key}`}>—</span> : null}
            {can(row) && (
              <>
                <button type="button" aria-label="Visit actions" data-testid={`visit-menu-${row.key}`}
                  onClick={(e) => { e.stopPropagation(); e.preventDefault(); setMenuFor(menuFor === row.key ? null : row.key); }}
                  className="px-1.5 py-0.5 rounded border border-purple-200 hover:bg-purple-100 text-purple-600"><MoreHorizontal size={14} /></button>
                {menuFor === row.key && (
                  <div className="absolute right-0 top-7 z-20 whitespace-nowrap bg-white border border-slate-200 rounded-lg shadow-lg py-1">{buttons(row, true)}</div>
                )}
              </>
            )}
          </div>
        </div>
        {/* the Raintree-only hint and item 7's label: Upcoming rows only */}
        {inUpcoming && row.raintree_only_hint && (
          <p className={`text-xs ${row.cancel_not_applied ? "text-amber-700 font-medium" : "text-slate-400"}`}
            data-testid={row.cancel_not_applied ? `visit-cancel-not-applied-${row.key}` : `visit-raintree-only-${row.key}`}>{row.raintree_only_hint}</p>
        )}
        {open && (
          <div className="mt-1 text-xs text-slate-500" data-testid={`visit-details-${row.key}`}>
            {info.map((t, i) => <p key={i}>{t}</p>)}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="col-span-2 p-3 rounded-xl bg-purple-50 border border-purple-200" data-testid="appointments-banner">
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <History size={14} className="text-purple-600" />
          <p className="text-xs font-semibold text-purple-700 uppercase tracking-wide" data-testid="next-visit-title">
            {next ? "Next visit" : "No upcoming visit"}
          </p>
        </div>
        <div className="flex items-center gap-1.5 text-xs text-purple-500" data-testid="live-visits-status">
          {status && status.startsWith("Couldn") && <AlertTriangle size={11} className="text-amber-600" />}
          <span>{status}</span>
          {!checking && status && status.startsWith("Checked") && (
            <button type="button" onClick={(e) => { e.stopPropagation(); e.preventDefault(); onRefresh(); }}
              className="inline-flex items-center gap-1 text-purple-600 hover:text-purple-800" data-testid="live-visits-refresh">
              · Refresh <RefreshCw size={11} />
            </button>
          )}
          {!checking && status && status.startsWith("Couldn") && (
            <button type="button" onClick={(e) => { e.stopPropagation(); e.preventDefault(); onRefresh(); }}
              className="text-purple-600 hover:text-purple-800" data-testid="live-visits-refresh">· Try again</button>
          )}
        </div>
      </div>

      {notice && (
        // The ONE message for a cancel Raintree didn't take (A4); Raintree's
        // reason, if any, is the muted second line. Stays until dismissed.
        <div className="mb-2 flex items-start justify-between gap-2 p-2 rounded-lg bg-red-100 text-red-800" role="alert" data-testid="cancel-notice">
          <div className="text-xs">
            <p>{notice.message}</p>
            {notice.description && <p className="text-red-700/80">{notice.description}</p>}
          </div>
          <button type="button" aria-label="Dismiss" onClick={(e) => { e.stopPropagation(); e.preventDefault(); onDismissNotice(); }}
            className="text-red-700 hover:text-red-900"><X size={12} /></button>
        </div>
      )}

      {next && (
        <div className="flex flex-wrap items-start justify-between gap-3 p-2 rounded-lg bg-white border border-purple-200" data-testid="next-visit">
          <div className="min-w-0">
            <p className="text-sm font-semibold text-purple-900">{when(next.appointment_datetime)}</p>
            <p className="text-xs text-purple-700"><TypeText row={next} />{next.provider_name && <> with {next.provider_name}</>}</p>
            {nextClinic && (
              <p className="text-xs text-slate-500">
                {[clinicText(nextClinic, next), nextClinic.address].filter(Boolean).join(" · ")}
                {nextClinic.phone && <> · <a href={`tel:${nextClinic.phone}`} className="hover:underline font-mono">{nextClinic.phone}</a></>}
              </p>
            )}
            <BookedBy row={next} />
            {next.raintree_only_hint && <p className={`text-xs ${next.cancel_not_applied ? "text-amber-700 font-medium" : "text-slate-400"}`}
              data-testid={next.cancel_not_applied ? "next-cancel-not-applied" : "next-raintree-only"}>{next.raintree_only_hint}</p>}
            <TextLine row={next} />
          </div>
          <div className="flex items-center gap-1.5 shrink-0">
            <Chip row={next} />
            {can(next) && buttons(next, false)}
          </div>
        </div>
      )}

      {/* W3 item 13: Upcoming (soonest first) / Past visits (newest first, 3 then
          "show n more") / Cancelled (folded, future cancels included) */}
      {upcoming.length > 0 && (
        <div className="mt-2" data-testid="upcoming-visits">
          <p className="text-xs font-semibold text-purple-700">Upcoming ({upcoming.length})</p>
          {upcoming.map((row) => <Row key={row.key} row={row} inUpcoming />)}
        </div>
      )}
      {past.length > 0 && (
        <div className="mt-2" data-testid="past-visits">
          <p className="text-xs font-semibold text-purple-700">Past visits ({past.length})</p>
          {(allPast ? past : past.slice(0, PAST_SHOWN)).map((row) => <Row key={row.key} row={row} />)}
          {!allPast && past.length > PAST_SHOWN && (
            <button type="button" onClick={(e) => { e.stopPropagation(); e.preventDefault(); setAllPast(true); }}
              className="mt-1 text-xs text-purple-600 hover:text-purple-800" data-testid="more-past">
              show {past.length - PAST_SHOWN} more
            </button>
          )}
        </div>
      )}
      {cancelled.length > 0 && (
        <div className="mt-2" data-testid="cancelled-visits">
          <p className="text-xs font-semibold text-purple-700">
            Cancelled ({cancelled.length})
            <button type="button" onClick={(e) => { e.stopPropagation(); e.preventDefault(); setShowCancelled(!showCancelled); }}
              className="ml-1 font-normal text-purple-600 hover:text-purple-800" data-testid="show-cancelled">
              · {showCancelled ? "hide" : "show"}
            </button>
          </p>
          {showCancelled && cancelled.map((row) => <Row key={row.key} row={row} />)}
        </div>
      )}
      {!rows && upcoming.length + past.length > 0 && (
        <p className="mt-1 text-xs text-slate-400" data-testid="states-pending">Visit states show once Raintree answers</p>
      )}
    </div>
  );
}
